/**
 * Zone2CreateModals — Batch #14.214
 *
 * Modals admin pour CRÉER (au nom du client) les 3 types de demandes :
 *  - Enquête de crédit
 *  - RL-31 (présence au 31 décembre)
 *  - Hausse de loyer
 *
 * Utilise les endpoints admin :
 *   POST /api/admin/immeuble/{master_id}/credit-checks
 *   POST /api/admin/immeuble/{master_id}/rl31-requests
 *   POST /api/admin/immeuble/{master_id}/rent-increase-requests
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, X, Plus, Trash2, Shield, FileText, FileSignature } from "lucide-react";
import { API, authH } from "@/api";


function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function ModalShell({ title, icon: Icon, tone, onClose, children, footer }) {
  const tones = {
    emerald: "from-emerald-600 to-emerald-700",
    amber: "from-amber-600 to-amber-700",
    indigo: "from-indigo-600 to-indigo-700",
  };
  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="zone2-create-modal">
        <div className={`px-5 py-3 bg-gradient-to-r ${tones[tone] || tones.emerald} text-white flex items-center justify-between sticky top-0 z-10`}>
          <h3 className="text-base font-bold inline-flex items-center gap-2">
            <Icon className="w-5 h-5" /> {title}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">{children}</div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2 sticky bottom-0">
          {footer}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CRÉER / MODIFIER ENQUÊTE DE CRÉDIT
// ════════════════════════════════════════════════════════════════════════════
export function CreateCreditCheckModal({ masterId, units, existing, onClose, onCreated }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    candidate_first_name: existing?.candidate_first_name || "",
    candidate_last_name: existing?.candidate_last_name || "",
    candidate_email: existing?.candidate_email || "",
    candidate_phone: existing?.candidate_phone || "",
    candidate_dob: existing?.candidate_dob || "",
    candidate_address_civic_number: existing?.candidate_address_civic_number || "",
    candidate_address_street: existing?.candidate_address_street || "",
    candidate_address_label: existing?.candidate_address_label || "",
    candidate_address_city: existing?.candidate_address_city || "",
    candidate_address_postal_code: existing?.candidate_address_postal_code || "",
    candidate_employer: existing?.candidate_employer || "",
    candidate_monthly_income: existing?.candidate_monthly_income ?? "",
    unit_id: existing?.unit_id || "",
    notes: existing?.notes || "",
    admin_notes: existing?.admin_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.candidate_monthly_income === "") payload.candidate_monthly_income = null;
      let r;
      if (isEdit) {
        r = await axios.put(`${API}/admin/immeuble/credit-checks/${existing.id}`, payload, { headers: authH() });
        toast.success("Enquête mise à jour");
      } else {
        r = await axios.post(`${API}/admin/immeuble/${masterId}/credit-checks`, payload, { headers: authH() });
        toast.success("Enquête de crédit créée");
      }
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Modifier l'enquête de crédit" : "Nouvelle enquête de crédit"}
      icon={Shield}
      tone="emerald"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="cc-modal-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="cc-modal-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Prénom du candidat" required>
          <input type="text" value={form.candidate_first_name} onChange={(e) => upd("candidate_first_name", e.target.value)} className="cc-input" maxLength={100} data-testid="cc-form-first-name" />
        </Field>
        <Field label="Nom" required>
          <input type="text" value={form.candidate_last_name} onChange={(e) => upd("candidate_last_name", e.target.value)} className="cc-input" maxLength={100} data-testid="cc-form-last-name" />
        </Field>
        <Field label="Courriel" required>
          <input type="email" value={form.candidate_email} onChange={(e) => upd("candidate_email", e.target.value)} className="cc-input" maxLength={120} data-testid="cc-form-email" />
        </Field>
        <Field label="Téléphone" required>
          <input type="tel" value={form.candidate_phone} onChange={(e) => upd("candidate_phone", e.target.value)} className="cc-input" maxLength={30} data-testid="cc-form-phone" />
        </Field>
        <Field label="Date de naissance" required>
          <input type="date" value={form.candidate_dob} onChange={(e) => upd("candidate_dob", e.target.value)} className="cc-input" data-testid="cc-form-dob" />
        </Field>
        <Field label="Logement visé">
          <select value={form.unit_id} onChange={(e) => upd("unit_id", e.target.value)} className="cc-input">
            <option value="">— Optionnel —</option>
            {(units || []).map((u) => (
              <option key={u.id} value={u.id}>
                {u._building?.name || "Immeuble"} · Apt {u.unit_label || u.id.slice(0, 6)}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="pt-2 border-t">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Adresse actuelle du candidat *</h4>
        <div className="grid grid-cols-3 gap-3">
          <Field label="N° civique" required>
            <input type="text" value={form.candidate_address_civic_number} onChange={(e) => upd("candidate_address_civic_number", e.target.value)} className="cc-input" maxLength={20} data-testid="cc-form-civic" />
          </Field>
          <Field label="Rue" required>
            <input type="text" value={form.candidate_address_street} onChange={(e) => upd("candidate_address_street", e.target.value)} className="cc-input col-span-2" maxLength={200} data-testid="cc-form-street" />
          </Field>
          <Field label="App. (optionnel)">
            <input type="text" value={form.candidate_address_label} onChange={(e) => upd("candidate_address_label", e.target.value)} className="cc-input" maxLength={20} data-testid="cc-form-label" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="Ville" required>
            <input type="text" value={form.candidate_address_city} onChange={(e) => upd("candidate_address_city", e.target.value)} className="cc-input" maxLength={100} data-testid="cc-form-city" />
          </Field>
          <Field label="Code postal" required>
            <input type="text" value={form.candidate_address_postal_code} onChange={(e) => upd("candidate_address_postal_code", e.target.value)} className="cc-input font-mono" maxLength={10} data-testid="cc-form-postal" />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t grid grid-cols-2 gap-3">
        <Field label="Employeur">
          <input type="text" value={form.candidate_employer} onChange={(e) => upd("candidate_employer", e.target.value)} className="cc-input" maxLength={200} />
        </Field>
        <Field label="Revenu mensuel ($)">
          <input type="number" step="0.01" value={form.candidate_monthly_income} onChange={(e) => upd("candidate_monthly_income", e.target.value)} className="cc-input font-mono" />
        </Field>
      </div>

      <Field label="Notes (visibles côté client)">
        <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={1000} />
      </Field>
      <Field label="Notes admin (internes)">
        <textarea rows={2} value={form.admin_notes} onChange={(e) => upd("admin_notes", e.target.value)} className="cc-input" maxLength={2000} data-testid="cc-form-admin-notes" />
      </Field>

      <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
        ⚠️ En créant cette enquête au nom du client, vous attestez avoir obtenu son consentement écrit ainsi que celui du candidat (Loi 25 / PIPEDA).
      </div>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CRÉER / MODIFIER RL-31
// ════════════════════════════════════════════════════════════════════════════
export function CreateRl31Modal({ masterId, properties, existing, onClose, onCreated }) {
  const isEdit = !!existing;
  // Pour edit : `existing` est la notification — on lit metadata.request_id et on charge la request détaillée
  const existingMeta = existing?.metadata || {};
  const [year, setYear] = useState(existingMeta.year || (new Date().getFullYear() - 1));
  const [propertyId, setPropertyId] = useState("");
  const [note, setNote] = useState("");
  const [tenants, setTenants] = useState([
    { name: "", address: "", total_rent_paid: "", has_daycare_service: false, daycare_amount: "", months_rented: 12 },
  ]);
  const [busy, setBusy] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);

  // Charge le détail de la request RL-31 si edit
  useEffect(() => {
    if (!isEdit) return;
    const reqId = existingMeta.request_id;
    if (!reqId) return;
    setLoadingExisting(true);
    (async () => {
      try {
        // GET /api/admin/immeuble/rl31-requests/{request_id}/detail (Batch #14.214)
        const detailRes = await axios.get(`${API}/admin/immeuble/rl31-requests/${reqId}/detail`, { headers: authH() }).catch(() => null);
        if (detailRes?.data) {
          const d = detailRes.data;
          setYear(d.year || year);
          setPropertyId(d.property_id || "");
          setNote(d.note || "");
          if (Array.isArray(d.tenants) && d.tenants.length > 0) {
            setTenants(d.tenants.map((t) => ({
              name: t.name || "",
              address: t.address || "",
              total_rent_paid: t.total_rent_paid ?? "",
              has_daycare_service: !!t.has_daycare_service,
              daycare_amount: t.daycare_amount ?? "",
              months_rented: t.months_rented ?? 12,
            })));
          }
        } else {
          // Fallback : seulement les noms depuis metadata
          const names = existingMeta.tenants_summary || [];
          if (names.length > 0) {
            setTenants(names.map((n) => ({
              name: n, address: "", total_rent_paid: "",
              has_daycare_service: false, daycare_amount: "", months_rented: 12,
            })));
          }
        }
      } finally { setLoadingExisting(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existing?.id]);

  const updTenant = (i, k, v) => {
    setTenants((prev) => prev.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  };
  const addTenant = () => setTenants((prev) => [...prev, { name: "", address: "", total_rent_paid: "", has_daycare_service: false, daycare_amount: "", months_rented: 12 }]);
  const removeTenant = (i) => setTenants((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    const cleaned = tenants
      .filter((t) => (t.name || "").trim())
      .map((t) => ({
        name: t.name.trim(),
        address: t.address || "",
        total_rent_paid: Number(t.total_rent_paid || 0),
        has_daycare_service: !!t.has_daycare_service,
        daycare_amount: Number(t.daycare_amount || 0),
        months_rented: Number(t.months_rented || 12),
      }));
    if (cleaned.length === 0) {
      toast.error("Au moins un locataire requis (avec un nom)");
      return;
    }
    setBusy(true);
    try {
      const body = {
        year: Number(year),
        property_id: propertyId || null,
        note,
        tenants: cleaned,
      };
      let r;
      if (isEdit) {
        r = await axios.put(`${API}/admin/immeuble/rl31-requests/${existing.id}`, body, { headers: authH() });
        toast.success("Demande RL-31 mise à jour");
      } else {
        r = await axios.post(`${API}/admin/immeuble/${masterId}/rl31-requests`, body, { headers: authH() });
        toast.success("Demande RL-31 créée");
      }
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Modifier la demande RL-31" : "Nouvelle demande RL-31"}
      icon={FileText}
      tone="amber"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="rl31-modal-cancel">Annuler</button>
          <button onClick={submit} disabled={busy || loadingExisting} className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="rl31-modal-submit">
            {(busy || loadingExisting) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Année fiscale" required>
          <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="cc-input font-mono" data-testid="rl31-form-year" />
        </Field>
        <Field label="Immeuble">
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="cc-input">
            <option value="">— Optionnel —</option>
            {(properties || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.address_line || p.id.slice(0, 6)}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Note interne (optionnel)">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="cc-input" maxLength={500} />
      </Field>

      <div className="pt-2 border-t">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">Locataires ayant occupé un logement au 31 décembre</h4>
          <button type="button" onClick={addTenant} className="text-[11px] text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" data-testid="rl31-add-tenant">
            <Plus className="w-3 h-3" /> Ajouter
          </button>
        </div>
        <div className="space-y-3">
          {tenants.map((t, i) => (
            <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid={`rl31-tenant-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800">Locataire #{i + 1}</span>
                {tenants.length > 1 && (
                  <button type="button" onClick={() => removeTenant(i)} className="text-rose-500 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Nom" required>
                  <input type="text" value={t.name} onChange={(e) => updTenant(i, "name", e.target.value)} className="cc-input" maxLength={200} data-testid={`rl31-tenant-name-${i}`} />
                </Field>
                <Field label="Adresse (logement)">
                  <input type="text" value={t.address} onChange={(e) => updTenant(i, "address", e.target.value)} className="cc-input" maxLength={300} />
                </Field>
                <Field label="Loyer total payé ($)">
                  <input type="number" step="0.01" value={t.total_rent_paid} onChange={(e) => updTenant(i, "total_rent_paid", e.target.value)} className="cc-input font-mono" />
                </Field>
                <Field label="Nb mois occupés">
                  <input type="number" min="0" max="12" value={t.months_rented} onChange={(e) => updTenant(i, "months_rented", e.target.value)} className="cc-input font-mono" />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={t.has_daycare_service} onChange={(e) => updTenant(i, "has_daycare_service", e.target.checked)} />
                Service de garde inclus
              </label>
              {t.has_daycare_service && (
                <Field label="Montant garde ($)">
                  <input type="number" step="0.01" value={t.daycare_amount} onChange={(e) => updTenant(i, "daycare_amount", e.target.value)} className="cc-input font-mono" />
                </Field>
              )}
            </div>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CRÉER / MODIFIER HAUSSE DE LOYER (renouvellement)
// ════════════════════════════════════════════════════════════════════════════
export function CreateRentIncreaseModal({ masterId, units, existing, onClose, onCreated }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    unit_id: existing?.unit_id || "",
    current_rent: existing?.current_rent ?? "",
    new_rent: existing?.new_rent ?? "",
    effective_date: existing?.effective_date || "",
    lease_renewal_date: existing?.lease_renewal_date || "",
    tenant_name: existing?.tenant_name || "",
    tenant_address: existing?.tenant_address || "",
    tenant_email: existing?.tenant_email || "",
    tenant_phone: existing?.tenant_phone || "",
    tenant_2_name: existing?.tenant_2_name || "",
    tenant_2_email: existing?.tenant_2_email || "",
    landlord_name: existing?.landlord_name || "",
    landlord_address: existing?.landlord_address || "",
    landlord_email: existing?.landlord_email || "",
    notes: existing?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-fill from selected unit (création uniquement)
  useEffect(() => {
    if (isEdit) return;
    if (!form.unit_id) return;
    const u = (units || []).find((x) => x.id === form.unit_id);
    if (u && !form.current_rent) {
      const rent = u._lease?.rent_monthly || u.rent_amount || u.monthly_rent;
      if (rent) upd("current_rent", String(rent));
    }
  }, [form.unit_id, units, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!isEdit && !form.unit_id) {
      toast.error("Choisir un logement");
      return;
    }
    if (!form.current_rent || !form.new_rent) {
      toast.error("Loyer actuel et nouveau loyer requis");
      return;
    }
    if (Number(form.new_rent) <= Number(form.current_rent)) {
      toast.error("Le nouveau loyer doit être supérieur à l'actuel");
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        current_rent: Number(form.current_rent),
        new_rent: Number(form.new_rent),
      };
      let r;
      if (isEdit) {
        // unit_id n'est pas modifiable en édition (clé de relation)
        delete body.unit_id;
        r = await axios.put(`${API}/admin/immeuble/rent-increase-requests/${existing.id}`, body, { headers: authH() });
        toast.success("Demande mise à jour");
      } else {
        r = await axios.post(`${API}/admin/immeuble/${masterId}/rent-increase-requests`, body, { headers: authH() });
        toast.success("Demande de hausse créée");
      }
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Modifier la hausse de loyer" : "Nouvelle hausse de loyer"}
      icon={FileSignature}
      tone="indigo"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="ri-modal-cancel">Annuler</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="ri-modal-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {isEdit ? "Enregistrer" : "Créer"}
          </button>
        </>
      }
    >
      <Field label="Logement concerné" required>
        <select
          value={form.unit_id}
          onChange={(e) => upd("unit_id", e.target.value)}
          className="cc-input"
          data-testid="ri-form-unit"
          disabled={isEdit}
        >
          <option value="">— Choisir —</option>
          {(units || []).map((u) => (
            <option key={u.id} value={u.id}>
              {u._building?.name || "Immeuble"} · Apt {u.unit_label || u.id.slice(0, 6)}
            </option>
          ))}
        </select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Loyer actuel ($/mois)" required>
          <input type="number" step="0.01" value={form.current_rent} onChange={(e) => upd("current_rent", e.target.value)} className="cc-input font-mono" data-testid="ri-form-current" />
        </Field>
        <Field label="Nouveau loyer ($/mois)" required>
          <input type="number" step="0.01" value={form.new_rent} onChange={(e) => upd("new_rent", e.target.value)} className="cc-input font-mono" data-testid="ri-form-new" />
        </Field>
        <Field label="Date d'effet">
          <input type="date" value={form.effective_date} onChange={(e) => upd("effective_date", e.target.value)} className="cc-input" />
        </Field>
        <Field label="Date de renouvellement bail">
          <input type="date" value={form.lease_renewal_date} onChange={(e) => upd("lease_renewal_date", e.target.value)} className="cc-input" />
        </Field>
      </div>

      <div className="pt-2 border-t">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Locataire principal</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom complet">
            <input type="text" value={form.tenant_name} onChange={(e) => upd("tenant_name", e.target.value)} className="cc-input" maxLength={200} data-testid="ri-form-tenant-name" />
          </Field>
          <Field label="Adresse">
            <input type="text" value={form.tenant_address} onChange={(e) => upd("tenant_address", e.target.value)} className="cc-input" maxLength={300} />
          </Field>
          <Field label="Courriel">
            <input type="email" value={form.tenant_email} onChange={(e) => upd("tenant_email", e.target.value)} className="cc-input" maxLength={120} />
          </Field>
          <Field label="Téléphone">
            <input type="tel" value={form.tenant_phone} onChange={(e) => upd("tenant_phone", e.target.value)} className="cc-input" maxLength={30} />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Co-locataire (optionnel)</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom complet">
            <input type="text" value={form.tenant_2_name} onChange={(e) => upd("tenant_2_name", e.target.value)} className="cc-input" maxLength={200} />
          </Field>
          <Field label="Courriel">
            <input type="email" value={form.tenant_2_email} onChange={(e) => upd("tenant_2_email", e.target.value)} className="cc-input" maxLength={120} />
          </Field>
        </div>
      </div>

      <div className="pt-2 border-t">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">
          Propriétaire (auto-rempli depuis le dossier — modifiable)
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom">
            <input type="text" value={form.landlord_name} onChange={(e) => upd("landlord_name", e.target.value)} className="cc-input" maxLength={200} placeholder="Auto depuis dossier" />
          </Field>
          <Field label="Adresse">
            <input type="text" value={form.landlord_address} onChange={(e) => upd("landlord_address", e.target.value)} className="cc-input" maxLength={300} placeholder="Auto depuis dossier" />
          </Field>
          <Field label="Courriel">
            <input type="email" value={form.landlord_email} onChange={(e) => upd("landlord_email", e.target.value)} className="cc-input" maxLength={120} />
          </Field>
        </div>
      </div>

      <Field label="Notes">
        <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" maxLength={1000} />
      </Field>
    </ModalShell>
  );
}
